import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  BANK_EDIT_ROLES,
  SELLER_ENTITY_LABELS_RU,
  parseBankStatement,
  type BankStatementDto,
  type InvoiceRegistryResultDto,
  type SellerEntity,
} from '@crm/shared'
import {
  getBankAccounts,
  getBankStatements,
  getOpenBankLines,
  uploadBankStatement,
  uploadInvoiceRegistry,
} from '../../shared/api/bank.api'
import { loadXlsx } from '../../shared/utils/loadXlsx'
import { apiErrorText } from '../../shared/api/errorText'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { toast } from '../../shared/ui/toastStore'
import { BrokerGrid } from '../customs/broker/BrokerGrid'
import { BankAccountsCard } from './BankAccountsCard'
import { BankStartDateCard } from './BankStartDateCard'
import { BankEntityPrompt } from './BankEntityPrompt'
import { BankInvoiceRegistryModal } from './BankInvoiceRegistryModal'
import { BankLinesTable } from './BankLinesTable'
import { parseInvoiceRegistry } from './parseInvoiceRegistry'
import { readStatementFile } from './readStatementFile'
import {
  BANK_ACCOUNTS_KEY,
  BANK_OPEN_LINES_KEY,
  BANK_STATEMENTS_KEY,
  formatBankDate,
  formatBankMoney,
  formatBankPeriod,
} from './bankFormat'

type StatementRow = {
  id: string
  uploadedLabel: string
  uploadedAt: string
  period: string
  account: string
  entity: string
  totalIn: number | null
  totalOut: number | null
  lines: string
  pendingCount: number
  uploadedByName: string
  fileName: string
}

function toRow(s: BankStatementDto): StatementRow {
  return {
    id: s.id,
    uploadedLabel: formatBankDate(s.uploadedAt),
    uploadedAt: s.uploadedAt,
    period: formatBankPeriod(s.periodFrom, s.periodTo),
    account: s.account.accountNumber,
    entity: s.account.entityId ? SELLER_ENTITY_LABELS_RU[s.account.entityId] : 'Не указано',
    totalIn: s.totalIn,
    totalOut: s.totalOut,
    lines: s.linesNew === s.linesTotal ? String(s.linesTotal) : `${s.linesNew} из ${s.linesTotal}`,
    pendingCount: s.pendingCount,
    uploadedByName: s.uploadedByName ?? '',
    fileName: s.fileName,
  }
}

const money = ({ value }: { value: number | null }) => formatBankMoney(value)

/**
 * Раздел «Банк» (этап 3, Б2): бухгалтер раз в день загружает банковскую выписку
 * (выгрузка клиент-банка для 1С). Программа отсеивает списания, депозиты и
 * возвраты, не задваивает строки при повторной загрузке и сама разносит по заказам
 * поступления, в которых уверена; остальное ждёт в очереди «Не разнесено» (Б3).
 */
export function BankPage() {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = !!role && (BANK_EDIT_ROLES as string[]).includes(role)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const fileRef = useRef<HTMLInputElement>(null)
  const registryRef = useRef<HTMLInputElement>(null)
  const [registry, setRegistry] = useState<InvoiceRegistryResultDto | null>(null)
  const [askEntity, setAskEntity] = useState<{
    fileName: string
    content: string
    accountNumber: string
    ownerName: string
  } | null>(null)
  const [uploading, setUploading] = useState(false)
  const [view, setView] = useState<'queue' | 'statements'>('statements')

  const statements = useQuery({ queryKey: [...BANK_STATEMENTS_KEY], queryFn: getBankStatements })
  const accounts = useQuery({ queryKey: [...BANK_ACCOUNTS_KEY], queryFn: getBankAccounts })
  const openLines = useQuery({ queryKey: [...BANK_OPEN_LINES_KEY], queryFn: getOpenBankLines })
  const rows = useMemo(() => (statements.data ?? []).map(toRow), [statements.data])
  const openCount = openLines.data?.length ?? 0
  const openSum = (openLines.data ?? []).reduce((s, l) => s + l.remaining, 0)

  const refreshAll = () => {
    for (const key of [BANK_STATEMENTS_KEY, BANK_ACCOUNTS_KEY, BANK_OPEN_LINES_KEY]) {
      void qc.invalidateQueries({ queryKey: [...key] })
    }
  }

  const upload = async (fileName: string, content: string, entityId?: SellerEntity) => {
    setUploading(true)
    try {
      const result = await uploadBankStatement(fileName, content, entityId)
      refreshAll()
      if (!result.statementId) {
        toast.info(
          `Эта выписка уже загружена: все строки (${result.linesTotal}) есть в программе, новых нет`,
        )
        return
      }
      const skipped = result.linesTotal - result.linesNew
      toast.success(
        `Выписка загружена: новых строк — ${result.linesNew}` +
          (skipped > 0 ? `, уже были загружены раньше — ${skipped}` : '') +
          (result.autoAllocated > 0
            ? `; разнесено по заказам само — ${result.autoAllocated}`
            : '') +
          (result.beforeStart > 0
            ? `; не учитываем, счёт до начала учёта — ${result.beforeStart}`
            : ''),
      )
      void navigate(`/bank/statements/${result.statementId}`)
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось загрузить выписку'))
    } finally {
      setUploading(false)
    }
  }

  const onFile = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    let content: string
    try {
      content = await readStatementFile(file)
    } catch {
      toast.error('Не удалось прочитать файл выписки')
      return
    }
    // Счёт без юрлица (новый или ещё не размеченный) — спрашиваем до загрузки,
    // иначе первая выписка не разнесётся по номерам счетов.
    try {
      const parsed = parseBankStatement(content)
      const known = (accounts.data ?? []).find((a) => a.accountNumber === parsed.accountNumber)
      if (!known?.entityId) {
        setAskEntity({
          fileName: file.name,
          content,
          accountNumber: parsed.accountNumber,
          ownerName: parsed.ownerName,
        })
        return
      }
    } catch {
      // Не выписка — понятную причину вернёт сервер.
    }
    await upload(file.name, content)
  }

  const onRegistryFile = async (file: File | undefined) => {
    if (registryRef.current) registryRef.current.value = ''
    if (!file) return
    setUploading(true)
    try {
      const XLSX = await loadXlsx()
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true })
      const sheet = book.Sheets[book.SheetNames[0] ?? '']
      if (!sheet) throw new Error('В файле нет листов')
      const rows = parseInvoiceRegistry(
        XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null }),
      )
      setRegistry(await uploadInvoiceRegistry(rows))
      refreshAll()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось загрузить счета 1С'))
    } finally {
      setUploading(false)
    }
  }

  const colDefs = useMemo<ColDef<StatementRow>[]>(
    () => [
      {
        field: 'period',
        headerName: 'Период выписки',
        minWidth: 170,
        cellRenderer: ({ data }: ICellRendererParams<StatementRow>) =>
          data ? (
            <Link
              to={`/bank/statements/${data.id}`}
              className="font-medium text-brand-600 hover:text-brand-700"
            >
              {data.period}
            </Link>
          ) : null,
      },
      { field: 'entity', headerName: 'Юрлицо', minWidth: 190 },
      { field: 'account', headerName: 'Расчётный счёт', minWidth: 200, cellClass: 'font-mono' },
      {
        field: 'totalIn',
        headerName: 'Поступило',
        minWidth: 140,
        filter: false,
        valueFormatter: money,
        cellClass: 'text-right tabular-nums',
        headerClass: 'ag-right-aligned-header',
      },
      {
        field: 'totalOut',
        headerName: 'Списано',
        minWidth: 130,
        filter: false,
        valueFormatter: money,
        cellClass: 'text-right tabular-nums',
        headerClass: 'ag-right-aligned-header',
      },
      { field: 'lines', headerName: 'Новых строк', minWidth: 120, filter: false },
      {
        field: 'pendingCount',
        headerName: 'К разносу',
        minWidth: 110,
        filter: false,
        cellClass: 'text-right tabular-nums',
        headerClass: 'ag-right-aligned-header',
      },
      {
        field: 'uploadedLabel',
        headerName: 'Загружена',
        minWidth: 120,
        filterParams: { dateMode: true },
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data?.uploadedAt ?? '').localeCompare(nodeB.data?.uploadedAt ?? ''),
      },
      { field: 'uploadedByName', headerName: 'Кто загрузил', minWidth: 160 },
      { field: 'fileName', headerName: 'Файл', flex: 1, minWidth: 200 },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-900">Банк</h1>
          {statements.data && <span className="text-sm text-gray-500">выписок: {rows.length}</span>}
        </div>
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".txt,text/plain"
              className="hidden"
              aria-label="Файл выписки"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            <input
              ref={registryRef}
              type="file"
              accept=".xls,.xlsx"
              className="hidden"
              aria-label="Файл счетов 1С"
              onChange={(e) => void onRegistryFile(e.target.files?.[0])}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => registryRef.current?.click()}
              title="Реестр счетов покупателям из журнала документов 1С (Excel): номера счетов запишутся в заказы"
              className="px-3 py-1.5 border border-gray-300 bg-white text-sm text-gray-700 rounded-lg hover:border-brand-500 transition-colors disabled:opacity-60"
            >
              Загрузить счета 1С
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-60"
            >
              {uploading ? 'Загружаю…' : 'Загрузить выписку'}
            </button>
          </div>
        ) : (
          <span className="text-xs text-gray-500">Просмотр: выписки загружает бухгалтерия</span>
        )}
      </div>

      <BankStartDateCard canEdit={canEdit} />

      {accounts.data && <BankAccountsCard accounts={accounts.data} canEdit={canEdit} />}

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['statements', `Выписки (${rows.length})`],
            [
              'queue',
              `Не разнесено (${openCount})${openCount > 0 ? ` · ${formatBankMoney(openSum)}` : ''}`,
            ],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              view === id
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-gray-200 bg-white text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'queue' ? (
        openLines.isLoading ? (
          <PageLoader label="Загружаю очередь…" />
        ) : openLines.isError ? (
          <ErrorMessage error={openLines.error} />
        ) : openCount === 0 ? (
          <EmptyState
            title="Всё разнесено"
            description="Неразнесённых поступлений нет. Сюда попадают платежи, которые программа не смогла разнести сама, остатки и платежи без заказа."
          />
        ) : (
          <BankLinesTable
            lines={openLines.data ?? []}
            canEdit={canEdit}
            storageKey="crm_bank_queue_cols_v1"
            className="h-[calc(100vh-360px)] min-h-[320px]"
            invalidateKeys={[]}
            showStatement
          />
        )
      ) : statements.isLoading ? (
        <PageLoader label="Загружаю выписки…" />
      ) : statements.isError ? (
        <ErrorMessage error={statements.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Выписок пока нет"
          description={
            canEdit
              ? 'Выгрузите выписку из клиент-банка в формате для 1С (файл .txt) и загрузите её кнопкой выше. Для каждого расчётного счёта — свой файл.'
              : 'Выписки загружает бухгалтерия.'
          }
        />
      ) : (
        <BrokerGrid<StatementRow>
          rowData={rows}
          columnDefs={colDefs}
          storageKey="crm_bank_statements_cols_v1"
          className="h-[calc(100vh-360px)] min-h-[320px]"
          noRowsText="Нет выписок"
        />
      )}

      {askEntity && (
        <BankEntityPrompt
          accountNumber={askEntity.accountNumber}
          ownerName={askEntity.ownerName}
          onCancel={() => setAskEntity(null)}
          onPick={(entityId) => {
            const pending = askEntity
            setAskEntity(null)
            void upload(pending.fileName, pending.content, entityId)
          }}
        />
      )}
      {registry && (
        <BankInvoiceRegistryModal
          result={registry}
          onClose={() => setRegistry(null)}
          onChanged={refreshAll}
        />
      )}
    </div>
  )
}
